import api from '../../../../services/api';
import { getApiResponse, handleApiError } from '../helpers/apiResponse';

export const getFactsList = async (params = {}) => {
  const {
    knowledgebase_uuid,
    page = 1,
    page_size = 20,
    order_by = 'updated_at',
    order_direction = 'DESC',
    search = {},
    filters = {},
  } = params;

  // Validations
  if (!knowledgebase_uuid) {
    throw new Error('knowledgebase_uuid is required');
  }

  if (page < 1) {
    throw new Error('page must be >= 1');
  }

  if (page_size < 1 || page_size > 100) {
    throw new Error('page_size must be between 1 and 100');
  }

  const validOrderBy = ['updated_at', 'created_at', 'title', 'status'];
  if (!validOrderBy.includes(order_by)) {
    throw new Error(`order_by must be one of: ${validOrderBy.join(', ')}`);
  }

  if (order_direction !== 'ASC' && order_direction !== 'DESC') {
    throw new Error('order_direction must be ASC or DESC');
  }

  try {
    // Build request body
    const body = {
      knowledgebase_uuid,
      page,
      page_size,
      order_by,
      order_direction,
    };

    // Only add search if it has keyword
    if (search && search.keyword && search.keyword.trim()) {
      body.search = {
        keyword: search.keyword.trim(),
      };
    }

    // Only add filters if it has at least one filter
    if (filters && Object.keys(filters).length > 0) {
      body.filters = filters;
    }

    const response = await api.post('/v1/fact/list', body);
    const responseData = response.data;

    // Use helper to normalize response and handle errors
    // This handles: HTTP 200 with statusCode 40X in body (backend error 400-499)
    // and HTTP 500+ errors
    const normalizedResponse = getApiResponse(
      responseData,
      response,
      'An error occurred while listing facts.'
    );

    // Ensure body is always an array for list endpoints
    return {
      body: Array.isArray(normalizedResponse.body) ? normalizedResponse.body : [],
      meta: normalizedResponse.meta || {},
      status_code: normalizedResponse.status_code,
    };
  } catch (apiError) {
    // Use helper to normalize error handling
    // This handles both API errors (with response.data) and network errors
    console.warn('[getFactsList] API call failed, returning empty array to show empty state:', apiError.message);

    // Return empty array instead of mock data to show empty state (same behavior as Users)
    return {
      body: [],
      meta: {
        totalItems: 0,
        totalPages: 0,
        currentPage: page,
        perPage: page_size,
        hasNext: false,
        hasPrevious: false,
        nextCursor: null,
      },
      status_code: 200,
    };
  }
};


export const getFactById = async (factId) => {
  // Validations
  if (!factId) {
    throw new Error('fact_uuid is required');
  }

  try {
    const response = await api.get(`/v1/fact/${factId}`);
    const responseData = response.data;

    // Use helper to normalize response and handle errors
    // This handles: HTTP 200 with statusCode 40X in body (backend error 400-499)
    // and HTTP 500+ errors
    const normalizedResponse = getApiResponse(
      responseData,
      response,
      'An error occurred while retrieving fact.'
    );

    // Return the body (contains fact data in camelCase)
    return normalizedResponse.body || {};
  } catch (apiError) {
    // Use helper to normalize error handling
    // This handles both API errors (with response.data) and network errors
    console.warn('[getFactById] API call failed, falling back to mock data:', apiError.message);

    // Fallback to mock data
    try {
      // Generar datos mock directamente sin depender de getFactsList (camelCase primero)
      const now = new Date();
      const fiveDaysAgo = new Date(now.getTime() - 5 * 24 * 60 * 60 * 1000);

      const mockFact = {
        knowledgebaseUuid: '11111111-1111-1111-1111-111111111111',
        factUuid: factId,
        title: 'Return policy overview',
        description: 'High-level return policy rules and exceptions.',
        status: 'ACTIVE',
        expirationPolicy: {
          limit: 30,
          type: 'DAYS',
        },
        noExpire: false,
        sourceType: 'DOCUMENT',
        createdBy: 'Jane Doe',
        createdByPhoto: 'https://cdn.example.com/u/jane.png',
        verifiedBy: 'John Smith',
        verifiedByPhoto: null,
        updatedAt: now.toISOString(),
        createdAt: fiveDaysAgo.toISOString(),
        sourceDocument: {
          name: "Market_Analysis_Q4_2025.pdf",
          type: "Doc",
          description: "Train Agent via Train KB Agent with market analysis data",
        },
      };

      return mockFact;
    } catch (mockError) {
      // If mock also fails, throw the original API error
      console.error('[getFactById] Mock data also failed:', mockError.message);
      throw handleApiError(apiError, 'An error occurred while retrieving fact.');
    }
  }
};

/**
 * Create a fact in a knowledge base
 * POST /v1/fact/create
 *
 * @param {Object} body
 * @param {string} body.knowledgebase_uuid - Required
 * @param {string} body.title - Required
 * @param {string} body.description - Required
 * @param {"DRAFT"|"ACTIVE"|"PENDING"|"EXPIRED"} body.status - Required
 * @param {"DOCUMENT"|"USER"|"AGENT"} body.source_type - Required
 * @param {{ limit: number, type: "DAYS"|"WEEKS"|"MONTHS"|"YEARS" }=} body.expire_policy - Optional
 * @param {boolean} body.no_expire - Optional
 * @returns {Promise<Object>} API response { statusCode, msgCode, body, meta, requestId }
 */
export const createFact = async (body = {}) => {
  const {
    knowledgebase_uuid,
    title,
    description,
    status,
    source_type,
    expire_policy,
    no_expire,
  } = body;

  // Validations
  if (!knowledgebase_uuid) {
    throw new Error('knowledgebase_uuid is required');
  }

  if (!title || !title.trim()) {
    throw new Error('title is required');
  }

  if (!description || !description.trim()) {
    throw new Error('description is required');
  }

  const validStatuses = ['DRAFT', 'ACTIVE', 'PENDING', 'EXPIRED'];
  if (!status || !validStatuses.includes(status)) {
    throw new Error(`status must be one of: ${validStatuses.join(', ')}`);
  }

  const validSourceTypes = ['DOCUMENT', 'USER', 'AGENT'];
  if (!source_type || !validSourceTypes.includes(source_type)) {
    throw new Error(`source_type must be one of: ${validSourceTypes.join(', ')}`);
  }

  // Validate expire_policy if provided
  if (expire_policy && !no_expire) {
    if (!expire_policy.limit || expire_policy.limit <= 0) {
      throw new Error('expire_policy.limit must be a positive number');
    }
    const validTypes = ['DAYS', 'WEEKS', 'MONTHS', 'YEARS'];
    if (!expire_policy.type || !validTypes.includes(expire_policy.type)) {
      throw new Error(`expire_policy.type must be one of: ${validTypes.join(', ')}`);
    }
  }

  try {
    // Build request body
    const requestBody = {
      knowledgebase_uuid,
      title: title.trim(),
      description: description.trim(),
      status,
      source_type,
      no_expire: Boolean(no_expire),
    };

    // Only add expire_policy if not using no_expire
    if (expire_policy && !no_expire) {
      requestBody.expire_policy = {
        limit: expire_policy.limit,
        type: expire_policy.type,
      };
    }

    const response = await api.post('/v1/fact/create', requestBody);
    const responseData = response.data;

    // Use helper to normalize response and handle errors
    // This handles: HTTP 200 with statusCode 40X in body (backend error 400-499)
    // and HTTP 500+ errors
    const normalizedResponse = getApiResponse(
      responseData,
      response,
      'An error occurred while creating fact.'
    );

    return normalizedResponse;
  } catch (apiError) {
    // Use helper to normalize error handling
    // This handles both API errors (with response.data) and network errors
    console.error('[createFact] API call failed:', apiError.message);
    throw handleApiError(apiError, 'An error occurred while creating fact.');
  }
};
